import type { CreditBalanceType, FeatureType } from "schemas/agent-billing";

import * as React from "react";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@replohq/lattice/components/table/Table";
import { Tag } from "@replohq/lattice/components/tag/Tag";
import { cn } from "@replohq/lattice/utils/cn";
import { skipToken } from "@tanstack/react-query";
import { useCurrentWorkspaceId } from "@v2/contexts/WorkspaceDashboardContext";
import { agentBillingRouter } from "@v2/trpc/routes/agentBilling";
import { creditBalanceTypeSchema } from "schemas/agent-billing";

import {
  ALWAYS_FRESH,
  amountColorClass,
  formatDate,
  StripeLink,
} from "./shared";

type Transaction = {
  id: string;
  createdAt: Date;
  featureType: string;
  type: string;
  balanceType: string | null;
  amount: number;
  balanceAfter: number | null;
  stripeTransactionId: string | null;
};

const BALANCE_TYPE_LABELS: Record<CreditBalanceType, string> = {
  purchase: "Purchase Credit Transactions",
  base: "Base Credit Transactions",
};

const TransactionTable: React.FC<{
  label: string;
  transactions: Transaction[];
}> = ({ label, transactions }) => {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-muted text-xs font-semibold uppercase tracking-wide">
        {label}
      </span>
      <div className="max-h-[300px] overflow-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead UNSAFE_className="text-xs">Time</TableHead>
              <TableHead UNSAFE_className="text-xs">Feature</TableHead>
              <TableHead UNSAFE_className="text-xs">Source</TableHead>
              <TableHead UNSAFE_className="text-xs">Amount</TableHead>
              <TableHead UNSAFE_className="text-xs">Bal After</TableHead>
              <TableHead UNSAFE_className="text-xs">Reference</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {transactions.map((transaction) => (
              <TableRow key={transaction.id}>
                <TableCell>
                  <span className="whitespace-nowrap text-xs">
                    {formatDate(transaction.createdAt)}
                  </span>
                </TableCell>
                <TableCell>
                  <span className="font-mono text-xs">
                    {transaction.featureType}
                  </span>
                </TableCell>
                <TableCell>
                  <Tag variant="neutral" size="sm">
                    {transaction.type}
                  </Tag>
                </TableCell>
                <TableCell>
                  <span
                    className={cn(
                      "font-mono text-xs",
                      amountColorClass(transaction.amount),
                    )}
                  >
                    {transaction.amount > 0
                      ? `+${transaction.amount}`
                      : transaction.amount}
                  </span>
                </TableCell>
                <TableCell>
                  <span className="font-mono text-xs">
                    {transaction.balanceAfter ?? "--"}
                  </span>
                </TableCell>
                <TableCell>
                  <span className="block max-w-[200px] truncate text-xs">
                    <StripeLink value={transaction.stripeTransactionId} />
                  </span>
                </TableCell>
              </TableRow>
            ))}
            {transactions.length === 0 && (
              <TableRow>
                <TableCell colSpan={6}>
                  <span className="text-muted block text-center text-xs">
                    No transactions.
                  </span>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export const CreditTransactionsTable: React.FC = () => {
  const workspaceId = useCurrentWorkspaceId();
  const { data, isLoading } =
    agentBillingRouter.credits.findTransactions.useQuery(
      workspaceId
        ? {
            workspaceId,
            // Legacy cast - may or may not be necessary
            // eslint-disable-next-line replo/no-as-cast
            featureType: "credits" as FeatureType,
            limit: 50,
          }
        : skipToken,
      ALWAYS_FRESH,
    );

  if (isLoading) {
    return <div className="text-muted py-3 text-xs">Loading...</div>;
  }

  // Legacy cast - may or may not be necessary
  // eslint-disable-next-line replo/no-as-cast
  const transactions = (data?.transactions ?? []) as unknown as Transaction[];

  const grouped = transactions.reduce<Record<CreditBalanceType, Transaction[]>>(
    (acc, transaction) => {
      // Legacy cast - may or may not be necessary
      // eslint-disable-next-line replo/no-as-cast
      const key = transaction.balanceType as CreditBalanceType | null;
      if (key && key in acc) {
        acc[key].push(transaction);
      }
      return acc;
    },
    { purchase: [], base: [] },
  );

  return (
    <div className="flex flex-col gap-4">
      {creditBalanceTypeSchema.options.map((balanceType) => (
        <TransactionTable
          key={balanceType}
          label={BALANCE_TYPE_LABELS[balanceType]}
          transactions={grouped[balanceType]}
        />
      ))}
    </div>
  );
};
